import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of, switchMap, tap } from 'rxjs';

import { ENVIRONMENT } from '@ninety/mobile/platform/environments';
import { CurrentUserSelectors, LanguageSelectors, NotificationActions } from '@ninety/mobile/platform/state';

import { DiscussionListItem } from '../../models/discussion.models';
import { DiscussionListActions, DiscussionListEffectActions, DiscussionToolbarComponentActions } from '../actions';
import { selectAllDiscussionsLoaded, selectGetDiscussionListQueryParams } from '../selectors';

@Injectable()
export class DiscussionListEffects {
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly environment = inject(ENVIRONMENT);

  disableScrollEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DiscussionListActions.ionScrollEvent),
      concatLatestFrom(() => this.store.select(selectAllDiscussionsLoaded)),
      tap(([{ event }, allDiscussionsLoaded]) => {
        if (event?.target) {
          event.target.disabled = allDiscussionsLoaded;
        }
      }),
      filter(([, allDiscussionsLoaded]) => !allDiscussionsLoaded),
      map(([{ event }]) => DiscussionListEffectActions.discussionListRequested({ event }))
    )
  );

  completeScrollEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          DiscussionListEffectActions.discussionListRequestSucceeded,
          DiscussionListEffectActions.discussionListRequestFailed
        ),
        tap(({ event }) => event?.target?.complete())
      ),
    { dispatch: false }
  );

  setFilterToCurrentUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DiscussionListActions.discussionListInit),
      concatLatestFrom(() => this.store.select(CurrentUserSelectors.selectUserId)),
      filter(([, currentUserId]) => !!currentUserId),
      map(([, currentUserId]) =>
        DiscussionListEffectActions.discussionListInit({ currentUserId: currentUserId as string })
      )
    )
  );

  requestDiscussionsOn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DiscussionListActions.discussionListInit,
        DiscussionListActions.discussionRefresh,
        DiscussionToolbarComponentActions.sortDirectionChanged,
        DiscussionToolbarComponentActions.listFiltered
      ),
      map(() => DiscussionListEffectActions.discussionListRequested({}))
    )
  );

  requestDiscussions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DiscussionListEffectActions.discussionListRequested),
      concatLatestFrom(() => [this.store.select(selectGetDiscussionListQueryParams)]),
      switchMap(([{ event }, params]) =>
        this.http
          .get<{ items: DiscussionListItem[]; totalCount: number }>(
            `${this.environment.origin.monolith}/Conversations`,
            { params }
          )
          .pipe(
            map(({ items: data, totalCount: totalDiscussions }) =>
              DiscussionListEffectActions.discussionListRequestSucceeded({ data, totalDiscussions, event })
            ),
            catchError(() => of(DiscussionListEffectActions.discussionListRequestFailed({ event })))
          )
      )
    )
  );

  handleError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DiscussionListEffectActions.discussionListRequestFailed),
      concatLatestFrom(() => this.store.select(LanguageSelectors.discussionLanguage)),
      map(([, lang]) =>
        NotificationActions.showToast({
          options: {
            type: 'negative',
            message: `An error occurred while loading ${lang.items}. Please try again.`,
          },
        })
      )
    )
  );
}
